import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { DISCOVERY_RELAYS, discoverySignal } from '@/lib/appRelays';
import {
  BUSINESS_VERIFY_KIND,
  BUSINESS_VERIFY_CLAIM_D,
  VERIFY_GRANT_ISSUERS,
  verifyGrantDTag,
  parseVerificationGrant,
  parseVerifyClaim,
  computeVerificationState,
  type VerificationState,
} from '@/lib/businessVerify';

const EMPTY_STATE: VerificationState = {
  status: 'UNVERIFIED',
  grant: null,
  claim: null,
  startAt: 0,
  expiresAt: 0,
  daysUntilExpiry: 0,
};

/**
 * Read the verification state for a single business pubkey by fetching the
 * issuer grant(s) and the business's own payment claim(s).
 */
export function useBusinessVerification(businessPubkey: string | undefined) {
  const { nostr } = useNostr();
  const enabled = !!businessPubkey && /^[0-9a-f]{64}$/i.test(businessPubkey);
  const biz = enabled ? businessPubkey!.toLowerCase() : '';

  return useQuery({
    queryKey: ['nostr', 'business-verification', biz],
    enabled,
    queryFn: async (c): Promise<VerificationState> => {
      const group = nostr.group(DISCOVERY_RELAYS);

      const [grantEvents, claimEvents] = await Promise.all([
        group.query(
          [{
            kinds: [BUSINESS_VERIFY_KIND],
            authors: VERIFY_GRANT_ISSUERS,
            '#d': [verifyGrantDTag(biz)],
            limit: 10,
          }],
          { signal: discoverySignal(c.signal) },
        ),
        group.query(
          [{
            kinds: [BUSINESS_VERIFY_KIND],
            authors: [biz],
            '#d': [BUSINESS_VERIFY_CLAIM_D],
            limit: 5,
          }],
          { signal: discoverySignal(c.signal) },
        ),
      ]);

      let grant = null;
      for (const ev of grantEvents) {
        const g = parseVerificationGrant(ev);
        if (!g || g.businessPubkey !== biz) continue;
        if (!grant || g.issuedAt > grant.issuedAt) grant = g;
      }

      let claim = null;
      for (const ev of claimEvents) {
        const parsed = parseVerifyClaim(ev);
        if (!parsed) continue;
        if (!claim || parsed.claimedAt > claim.claimedAt) claim = parsed;
      }

      return computeVerificationState(grant, claim);
    },
    initialData: enabled ? undefined : EMPTY_STATE,
    staleTime: 5 * 60 * 1000,
  });
}
